import { Timeline, TimelineState } from '@xzdarcy/react-timeline-editor';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import './Timeline.less';
import {
  actionToMaterial,
  addTrackIndex,
  AudioMode,
  CustomAction,
  CustomRow,
  generateId,
  generateMaterialId,
  isAudioSubtitleType,
  isAudioType,
  isMusicType,
  isTranslationSubtitleType,
  isTranslationType,
  isVideoType,
  isSubtitle,
  materialToAction,
  RowType,
  showAudioAndDoubleSubtitle,
  showAudioAndTranslation,
  transformMaterialListToRowData,
  isProjectModelD,
  isProjectModelE,
  TypeMap,
  isNarrationMode,
  isTranslationSubtitle,
  setAuditionAction,
  getAuditionActionId,
  transformAudio,
  canSubtitleMove,
  canSyncSubtitle,
} from '@/components/Timeline/utils.ts';
import {
  Material,
  MaterialDetail,
  MaterialType,
  OperationType,
  ProjectMode,
  Subtitle,
  SubtitleSegmented,
} from '@/interface.ts';
import { EditContext } from '@/context/EditContext';
import { EditEventBus } from '@/components/Timeline/command.ts';
import CustomRenderActions from '@/components/Timeline/CustomRenderActions.tsx';
import { ActionEvent, effects, resetActionsPerTick } from '@/components/Timeline/effects.ts';
import { projectController } from '@/api/edit';
import Icon from '@/components/iconfont/Icon';
import { formatFromHourToSecond, formatFromMinuteToSecond } from '@/utils/dayjs';
import { useTranslation } from 'react-i18next';
import RangeSlider from 'react-range-slider-input';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import 'react-range-slider-input/dist/style.css';
export const SCALE_WIDTH = 250; // 时间轴一格主刻度的宽度，单位px
const ACTION_CLIP_TYPE = {
  [RowType.video]: 'video',
  [RowType.music]: 'audio',
  [RowType.audio]: 'speech',
  [RowType.audioSubtitle]: 'sub',
  [RowType.translation]: 'speech',
  [RowType.translationSubtitle]: 'sub',
};

const DEFAULT_SUBTITLE_SHOW = {
  [ProjectMode.TRANSLATION_TTS]: 2,
  [ProjectMode.TRANSLATION]: 2,
  [ProjectMode.SUBTITLE]: 1,
  [ProjectMode.SUBTITLE_TRANSLATION]: 3,
  [ProjectMode.REWRITE]: 1,
  [ProjectMode.NARRATION]: 1,
};

const isSupportCut = (item: CustomAction | CustomRow) => isVideoType(item) || isMusicType(item);
const isSupportSpeed = (item: CustomAction | CustomRow) =>
  isAudioType(item) || isTranslationType(item);
const isSupportVolume = (item: CustomAction | CustomRow) =>
  isMusicType(item) || isAudioType(item) || isTranslationType(item);
const isSupportHide = (item: CustomAction | CustomRow) =>
  isVideoType(item) || isAudioSubtitleType(item) || isTranslationSubtitleType(item);
const isSupportNewRow = (item: CustomAction | CustomRow) =>
  !isAudioSubtitleType(item) && !isTranslationSubtitleType(item);

function isActionOverlapNeighbor(row: CustomRow, action: CustomAction) {
  const actionIndex = row.actions.indexOf(action);
  const nextAction = row.actions[actionIndex + 1];
  const prevAction = row.actions[actionIndex - 1];
  let isOverlap = false;
  if (nextAction && action.end > nextAction.start) {
    isOverlap = true;
  }
  if (prevAction && action.start < prevAction.end) {
    isOverlap = true;
  }
  return isOverlap;
}
function getSentencesFromSegmenta(row: CustomRow) {
  return row.actions
    .reduce((acc: CustomAction[], action: CustomAction) => {
      const sentences = acc.find((res) => res.sentenceId === action.sentenceId);
      if (!sentences) {
        acc.push({
          ...action,
          end: action.subtitle_end,
          start: action.subtitle_start,
        } as CustomAction);
      }
      return acc;
    }, [])
    .filter((res) => res.start !== undefined && res.end !== undefined);
}
function canCurrentRowContainAction(row: CustomRow, action: CustomAction) {
  let copyActions;
  let operateAction;
  if (isSubtitle(action)) {
    const subtitle_row = getSentencesFromSegmenta(row);
    operateAction = subtitle_row.find((res) => res.sentenceId === action.sentenceId);
    copyActions = subtitle_row.filter((res) => res.sentenceId !== action.sentenceId);
  } else {
    const actionIndex = row.actions.indexOf(action);
    copyActions = [...row.actions];
    copyActions.splice(actionIndex, 1);
    operateAction = action;
  }
  return canOtherRowContainAction(
    {
      id: row.id,
      type: row.type,
      actions: copyActions,
    },
    operateAction!,
  );
}

function canOtherRowContainAction(row: CustomRow, action: CustomAction) {
  let canContain = true;
  let containIndex = 0;
  let conflictIndex = -1;
  for (; containIndex < row.actions.length; containIndex++) {
    const currentAction = row.actions[containIndex];
    if (containIndex === 0 && action.end <= currentAction.start) {
      break;
    }
    if (currentAction.end <= action.start) {
      const nextAction = row.actions[containIndex + 1];
      if (nextAction && nextAction.start >= action.end) {
        containIndex++;
        break;
      }
    } else {
      canContain = false;
      conflictIndex = containIndex;
      break;
    }
  }
  return {
    canContain,
    containIndex,
    conflictIndex,
  };
}

function findAction(data: CustomRow[], type: RowType, materialId: number) {
  const rows = data.filter((row: CustomRow) => row.type === type);
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const index = row.actions.findIndex((a) => a.materialId == materialId);
    const action = row.actions[index];
    if (action) {
      return {
        action,
        index,
        row,
      };
    }
  }
}

function findMaterialDetail(action: CustomAction, material: Material) {
  const index = material.details.findIndex(
    (detail) => detail.id == (action.sentenceId || action.materialId),
  );
  const detail = material.details[index];
  return {
    index,
    detail,
  };
}

function modifyMaterialWidthEffect(material: MaterialDetail, action: CustomAction) {
  const { start, end, from, to, speed, hide, volume, trackIndex, mute } =
    action as Required<CustomAction>;
  material.timeLineFrom = start * 1000;
  material.timeLineTo = end * 1000;
  material.from = from * 1000;
  material.to = to * 1000;
  material.speed = speed;
  material.volume = volume;
  material.hide = hide;
  material.trackIndex = trackIndex;
  material.mute = mute;
}

function modifySubtitleWidthEffect(material: MaterialDetail, action: CustomAction) {
  const { start, end } = action;
  material.subtitle.timeLineFrom = start * 1000;
  material.subtitle.timeLineTo = end * 1000;
}
function modifySubtitleSegmentedWidthEffect(material: MaterialDetail, actions: CustomAction[]) {
  material.subtitleSegmented?.sort(
    (a: SubtitleSegmented, b: SubtitleSegmented) =>
      a.timeLineFrom - b.timeLineFrom || a.timeLineTo - b.timeLineTo,
  );
  actions.sort((a, b) => a.start - b.start || a.end - b.end);
  material.subtitleSegmented
    ?.map((action: SubtitleSegmented, index) => {
      const timeLineAction = actions[index];
      if (timeLineAction) {
        action.timeLineFrom = Number((timeLineAction.start * 1000).toFixed(3));
        action.timeLineTo = Number((timeLineAction.end * 1000).toFixed(3));
      }
      return action;
    })
    ?.sort(
      (a: SubtitleSegmented, b: SubtitleSegmented) =>
        a.timeLineFrom - b.timeLineFrom || a.timeLineTo - b.timeLineTo,
    );
}

function calcCutTime(action: CustomAction, time: number) {
  const { start, from, speed } = action as Required<CustomAction>;
  const result = {
    currentEnd: time,
    currentTo: 0,
    nextStart: time,
    nextFrom: 0,
  };
  let realCutTime = 0;
  if (isSupportSpeed(action)) {
    realCutTime = (time - start) * speed + from;
  } else {
    realCutTime = time - start + from;
  }
  result.currentTo = realCutTime;
  result.nextFrom = realCutTime;
  return result;
}

function calcDefaultTimelineScale(duration: number, list: number[]) {
  const bodyWidth = document.body.clientWidth * 0.75;
  const scale = Math.ceil((SCALE_WIDTH * duration) / bodyWidth);
  const index = list.findIndex((s) => s <= scale);
  return {
    index,
    scale,
  };
}

function getDefaultSubtitleShow(mode: ProjectMode) {
  return DEFAULT_SUBTITLE_SHOW[mode];
}

function calcTrackIndex(data: CustomRow[], row: CustomRow) {
  const rows = data.filter((r) => r.type === row.type);
  const rowIndex = rows.indexOf(row);
  if (rows.length === 1 || rowIndex === 0) {
    return Math.ceil(row.trackIndex! + 100);
  } else {
    const aboveTrackIndex = rows[rowIndex - 1].trackIndex;
    return (aboveTrackIndex! + row.trackIndex!) / 2;
  }
}

function calcLastTime(data: CustomRow[]) {
  let lastTime = 0;
  data.forEach((row) => {
    const lastRowAction = row.actions.at(-1);
    if (lastRowAction && lastRowAction.end > lastTime) {
      lastTime = lastRowAction.end;
    }
  });
  return lastTime;
}

function calcScaleList(duration: number) {
  let i = Math.max(Math.floor((duration * SCALE_WIDTH) / 400), 1);
  const scaleList = [];
  while (i >= 1) {
    scaleList.push(i);
    i = Math.floor(i / 2);
  }
  if (scaleList.length === 1) {
    scaleList.unshift(2);
  }
  return scaleList;
}

function RowOperation(props: {
  row: CustomRow;
  visibleFn: (row: CustomRow) => void;
  volumeFn: (row: CustomRow) => void;
  mode: ProjectMode;
}) {
  const editContextValue = useContext(EditContext) as {
    activeTab: string;
  };
  const { row, visibleFn, volumeFn, mode } = props;
  const musicIncludevoice = (mode: ProjectMode) => {
    return isProjectModelD(mode) || isProjectModelE(mode) || isNarrationMode(mode);
  };
  const typeIcons = {
    [RowType.video]: 'icon-a-Videol',
    [RowType.music]: musicIncludevoice(mode) ? 'icon-a-PBGML' : 'icon-a-BGMl',
    [RowType.audio]: showAudioAndTranslation(mode) ? 'icon-speech_o' : 'icon-a-Voicel',
    [RowType.audioSubtitle]: 'icon-a-Subtitlel',
    [RowType.translation]: showAudioAndTranslation(mode) ? 'icon-speech_t' : 'icon-a-Voicel',
    [RowType.translationSubtitle]: 'icon-a-Subtitlel',
  };
  const [actionIcon, setActionIcon] = useState('');
  const [highLightClass, setHighLightClass] = useState('');
  const [selection, setSelection] = useState(false);
  useEffect(() => {
    if (isSupportHide(row)) {
      setActionIcon(row.hide ? 'icon-eyeclose' : 'icon-view');
      setHighLightClass(row.hide ? 'icon-highlight' : '');
    } else if (isAudioType(row) || isTranslationType(row)) {
      setActionIcon(row.mute ? 'icon-eyeclose' : 'icon-view');
      setHighLightClass(row.mute ? 'icon-highlight' : '');
    } else if (isMusicType(row)) {
      setActionIcon(row.mute ? 'icon-closeVolume' : 'icon-openVolume');
      setHighLightClass(row.mute ? 'icon-highlight' : '');
    }
  }, [row.type, row.volume, row.hide, row.mute]);
  useEffect(() => {
    switch (editContextValue.activeTab) {
      case 'speech':
        setSelection(isAudioType(row) || isTranslationType(row));
        break;
      case 'subtitle':
        setSelection(isSubtitle(row));
        break;
      default:
        break;
    }
  }, [editContextValue.activeTab]);
  function handleOperation() {
    if (isSupportHide(row)) {
      visibleFn && visibleFn(row);
    } else if (isSupportVolume(row)) {
      volumeFn && volumeFn(row);
    }
  }
  return (
    <div
      className={`row-operation ${isVideoType(row) || isMusicType(row) ? 'box38' : 'box26'} ${isTranslationType(row) || isAudioType(row) ? 'margTop6' : ''} ${row.actions.length === 0 ? 'hidden' : ''} ${selection && 'selectRow'}`}>
      <Icon name={typeIcons[row.type]} className="icon-left" />
      <Icon
        name={actionIcon}
        className={`icon-right ${highLightClass}`}
        onClick={handleOperation}
      />
    </div>
  );
}

function TimelineScaleController(props: {
  maxscale: number;
  minscale: number;
  onChange: (value: number) => void;
  timeToLeft: () => void;
  scale: number;
}) {
  const { maxscale, minscale, scale, onChange, timeToLeft } = props;
  const [scaleVal, setScaleVal] = useState<number>(scale * -1);
  useEffect(() => {
    setScaleVal(scale * -1);
  }, [scale]);
  function handleDecrease() {
    if (scaleVal * -1 >= maxscale) return;
    setScaleVal((scaleVal) => {
      onChange((scaleVal - 1) * -1);
      return scaleVal - 1;
    });
    timeToLeft();
  }

  function handleIncrease() {
    if (scaleVal * -1 <= minscale) return;
    setScaleVal((scaleVal) => {
      onChange((scaleVal + 1) * -1);
      return scaleVal + 1;
    });
    timeToLeft();
  }

  return (
    <div className="scale-controller">
      <div className="changeScaleBtn tooltip" data-tip="zoom out (-)">
        <Icon name="icon-reduce" onClick={handleDecrease}></Icon>
      </div>
      <div className="changeScaleBlock">
        <RangeSlider
          min={(maxscale || 0) * -1}
          max={(minscale || 0) * -1}
          step={1}
          value={[(maxscale || 0) * -1, scaleVal]}
          className="single-thumb"
          thumbsDisabled={[true, false]}
          rangeSlideDisabled={true}
          onInput={(e) => {
            setScaleVal(e[1]);
          }}
          onThumbDragEnd={() => {
            timeToLeft();
            onChange(scaleVal * -1);
          }}
        />
      </div>
      <div className="changeScaleBtn tooltip" data-tip="zoom out (+)">
        <Icon name="icon-add" onClick={handleIncrease}></Icon>
      </div>
    </div>
  );
}
function ChangeVolume(props: { action: CustomAction | undefined }) {
  const [volume, setVolume] = useState(0);
  const { action } = props;
  const changeVolume = (val: number) => {
    const sliderThumb = document.querySelector('.range-slider__thumb[data-upper]');
    if (sliderThumb instanceof HTMLElement) {
      sliderThumb.style.setProperty('--thumb-content', `"${val.toFixed(0)}"`);
    }
  };
  useEffect(() => {
    if (action?.volume != null) {
      changeVolume(action.volume * 100);
      setVolume(action.volume * 100);
    }
  }, [action, action?.volume]);
  return (
    <div className="volumeblock">
      <RangeSlider
        min={0}
        max={100}
        step={1}
        value={[0, volume]}
        className="single-thumb"
        thumbsDisabled={[true, false]}
        rangeSlideDisabled={true}
        onInput={(e) => {
          changeVolume(e[1]);
          setVolume(e[1]);
        }}
        onThumbDragEnd={() => {
          if (action) {
            action.volume = Number(volume) / 100;
            ActionEvent.emit('changeVolume', action);
          }
        }}
      />
    </div>
  );
}

function CustomScaleRenderer(props: { scale: number }) {
  const scale = props.scale * 1000;
  const hourMs = 60 * 60 * 1000;
  const time = scale >= hourMs ? formatFromHourToSecond(scale) : formatFromMinuteToSecond(scale);
  return <>{time}</>;
}

interface ControllerOptions {
  start: number;
  url: string;
  speed: number;
  end: number;
  id: number;
  isTranslation: boolean;
}

interface TimelineBusinessProps {
  getTempAudioController?: (fn: (options: ControllerOptions) => () => void) => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
}

enum ActionOperation {
  RESIZE = 'RESIZE',
  MOVE = 'MOVE',
}
export default function TimelineBusiness(props: TimelineBusinessProps) {
  const { getTempAudioController, onPlayStateChange } = props;
  const { t } = useTranslation();
  const timelineState = useRef<TimelineState>(null);
  const leftActions = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<CustomRow[]>([]);
  const [selectedAction, setSelectedAction] = useState<CustomAction>();
  const [draggingAction, setDraggingAction] = useState<CustomAction>();
  const [resizedAction, setResizedAction] = useState<CustomAction>();
  const [resizedDirection, setResizedDirection] = useState<string>('');
  const [time, setTime] = useState(0);
  // const [isPlaying, setIsPlaying] = useState(false);
  const [timelineScale, setTimelineScale] = useState(10);
  // const [defaultScaleIndex, setDefaultScaleIndex] = useState(0);
  const [scaleList, setScaleList] = useState<number[]>([]);
  const [canCut, setCanCut] = useState<boolean>(true);
  const [actionToCut, setActionToCut] = useState<CustomAction>();
  const actionClicked = useRef(false);
  const isFirstRender = useRef(true);
  const isCanChack = useRef(false);
  const lastTime = useRef(0);
  const timeLineScrollLeft = useRef(0);
  const timeLineClientWidth = useRef(0);
  const timeToLeft = useRef(0);
  const [showChangeVolume, setShowChangeVolume] = useState<boolean>(false);
  const [volumeAction, setVolumeAction] = useState<CustomAction | undefined>();
  const checkChangeVolume = useRef(0);
  const [resetWaveform, setResetWaveform] = useState<number>(0);
  const [disableDrag, setDisableDrag] = useState<boolean>(false);
  const [waveformCompleteWidth, setWaveformCompleteWidth] = useState<number>(0);
  const editContextValue = useContext(EditContext) as {
    setEditPageLoading(arg0: boolean): unknown;
    projectDetail: {
      id: number;
      duration: number;
      subtitleShow: number;
      mode: ProjectMode;
      usage: number;
      language: string;
      translationLanguage: string;
    };
    speakerColorList: { textColor: string; bgColor: string }[];
    sessionId: string;
    materialSorted: boolean;
    useTranslate: boolean;
    translationLanguage: string;
    speakerList: { speakerId: number }[];
    subtitleShow: number;
    isPlaying: boolean;
    setIsPlaying: (isPlaying: boolean) => boolean;
    setSubtitleShow: (show: number) => void;
    setTimelinePlaybackTime: (time: number) => void;
    isInputFocused: boolean;
    setActiveTab: (tab: string) => void;
    videoMaterial: Material;
    setVideoMaterial: (material: Material) => void;
    musicMaterial: Material;
    setMusicMaterial: (material: Material) => void;
    sentenceMaterial: Material;
    setSentenceMaterial: (material: Material) => void;
    translationMaterial: Material;
    setTranslationMaterial: (material: Material) => void;
    sortByTimeLineFrom: (details: MaterialDetail[]) => MaterialDetail[];
    subtitleSegmentationCount: number;
  };

  function initMaterialData() {
    const {
      projectDetail,
      videoMaterial,
      musicMaterial,
      sentenceMaterial,
      translationMaterial,
      subtitleShow,
    } = editContextValue;
    const rows = transformMaterialListToRowData(
      videoMaterial,
      musicMaterial,
      sentenceMaterial,
      translationMaterial,
      projectDetail.mode,
    );
    const filter = showAudioAndTranslation(projectDetail.mode)
      ? isTranslationSubtitleType
      : isAudioSubtitleType;
    const subtitleRow = rows.find(filter);
    if (subtitleRow) {
      subtitleRow.hide = !subtitleShow;
      subtitleRow.classNames = subtitleShow ? [] : ['opacity'];
    }
    const videoRows = rows.filter(isVideoType);
    videoRows.forEach((row) => {
      addTrackIndex(row.trackIndex!);
    });
    setData(rows);
    isCanChack.current = true;
    return rows;
  }
  useEffect(() => {
    if (!isCanChack.current) return;
    isCanChack.current = false;
    const mode = editContextValue.projectDetail.mode;
    if (!isProjectModelD(mode) && !isProjectModelE(mode)) {
      checkTrackData();
    }
  }, [data]);
  const checkTrackData = () => {
    const sentenceType = showAudioAndTranslation(editContextValue.projectDetail.mode)
      ? RowType.translation
      : RowType.audio;
    const subtitleType = showAudioAndTranslation(editContextValue.projectDetail.mode)
      ? RowType.translationSubtitle
      : RowType.audioSubtitle;
    const sentenceRows = data.filter((res) => res.type === sentenceType);
    const subtitleRow = data.find((res) => res.type === subtitleType);
    sentenceRows.forEach((row) => {
      for (let index = 0; index < row?.actions.length; index++) {
        const action = row?.actions[index];
        const prevAction = row.actions[index - 1];
        const nextAction = row.actions[index + 1];
        if (
          (!prevAction || action.start >= prevAction.end) &&
          (!nextAction || action.end <= nextAction.start)
        ) {
          continue;
        }
        moveActionToNewRow(action, row);
        index--;
        const subtitle_row = getSentencesFromSegmenta(subtitleRow!);
        const subtitle = subtitle_row.find((res) => res.sentenceId === action.materialId);
        let subtitles;
        if (subtitle && subtitleRow) {
          subtitles = calcSubtitleOverlapWithEffect(
            { ...subtitleRow!, actions: subtitle_row },
            subtitle,
          );
        }
        syncSubtitle(
          action,
          subtitle!,
          subtitles!.prevSubtitle,
          subtitles!.nextSubtitle,
          editContextValue.projectDetail.mode,
          isTranslationType(action),
        );
      }
    });
  };
  useEffect(() => {
    const engine = timelineState.current;
    if (editContextValue.materialSorted) {
      const data = initMaterialData();
      if (engine) {
        engine.listener.on('setTimeByTick', syncTime);
        engine.listener.on('afterSetTime', syncTime);
        engine.listener.on('play', handleEnginePlay);
        engine.listener.on('paused', handleEnginePause);
        engine.listener.on('ended', handleEngineEnd);
      }
      const duration = calcLastTime(data);
      lastTime.current = duration;
      const scaleList = calcScaleList(duration);
      const { scale } = calcDefaultTimelineScale(duration, scaleList);

      // 波形图图片方案-start
      // const maxTo = Math.max(...editContextValue.musicMaterial.details.map((item) => item.to));
      // const maxMusicMaterial = editContextValue.musicMaterial.details.find(
      //   (item) => item.to === maxTo,
      // ) || { to: 0 };
      // const waveImgWidth = (maxMusicMaterial.to / 1000 / scale) * 250;
      // transformWaveImage(editContextValue.musicMaterial, waveImgWidth).then(() => {
      //   setScaleList(scaleList);
      //   setTimelineScale(scale);
      //   getTempAudioController && getTempAudioController(tempAudioController);
      //   editContextValue.setEditPageLoading(false);
      //   setResetWaveform(Date.now());
      // });
      // 波形图图片方案-end

      // 波形图canvas方案-start
      setScaleList(scaleList);
      setTimelineScale(scale);
      getTempAudioController && getTempAudioController(tempAudioController);
      editContextValue.setEditPageLoading(false);
      setResetWaveform(Date.now());
      // 波形图canvas方案-end
    }
    return () => {
      if (engine) {
        engine.listener.off('setTimeByTick', syncTime);
        engine.listener.off('afterSetTime', syncTime);
        engine.listener.off('play', handleEnginePlay);
        engine.listener.off('paused', handleEnginePause);
        engine.listener.off('ended', handleEngineEnd);
        if (editContextValue.materialSorted) {
          engine.setTime(0);
          engine.pause();
          engine.listener.offAll();
          resetActionsPerTick();
        }
      }
    };
  }, [editContextValue.materialSorted]);

  useEffect(() => {
    const { subtitleShow, projectDetail } = editContextValue;
    const filter = isTranslationSubtitle(projectDetail.mode)
      ? isTranslationSubtitleType
      : isAudioSubtitleType;
    const subtitleRow = data.find(filter);
    if (subtitleRow) {
      subtitleRow.hide = !subtitleShow;
      subtitleRow.classNames = subtitleShow ? [] : ['opacity'];
      setData([...data]);
    }
  }, [editContextValue.subtitleShow, editContextValue.projectDetail.mode]);

  useEffect(() => {
    const handleSpaceBar = (e: KeyboardEvent) => {
      if (e.code.toLowerCase() !== 'space' || editContextValue.isInputFocused) return;
      e.preventDefault();
      e.stopPropagation();
      if (editContextValue.isPlaying) {
        pause();
      } else {
        play();
      }
    };
    document.addEventListener('keydown', handleSpaceBar, true);
    return () => {
      document.removeEventListener('keydown', handleSpaceBar, true);
    };
  }, [editContextValue.isInputFocused, editContextValue.isPlaying]);

  function syncTime(data: { time: number }) {
    const { time } = data;
    editContextValue.setTimelinePlaybackTime(time * 1000);
    setTime(Number(time.toFixed(3)));
  }

  function handleEnginePlay() {
    editContextValue.setIsPlaying(true);
  }

  function handleEnginePause() {
    editContextValue.setIsPlaying(false);
  }

  function handleEngineEnd() {
    if (timelineState.current && !getAuditionActionId()) {
      timelineState.current.setTime(0);
    }
  }

  function tempAudioController(options: ControllerOptions) {
    const { start, end, id, isTranslation } = options;
    setAuditionAction({ id, isTranslation });
    setTimeout(() => {
      timelineState.current?.pause();
      timelineState.current?.setTime(start);
      timelineState.current && timelineState.current?.play({ toTime: end });
    }, 10);
    return () => {
      timelineState.current?.pause();
    };
  }

  useEffect(() => {
    if (!editContextValue.isPlaying) {
      setAuditionAction({ id: 0 });
    }
    onPlayStateChange && onPlayStateChange(editContextValue.isPlaying);
  }, [editContextValue.isPlaying]);

  const modifyActionText = useCallback(
    (type: RowType, materialId: number, text: string) => {
      const result = findAction(data, type, materialId);
      if (result) {
        result.action.text = text;
      }
    },
    [data],
  );
  const modifyActionEdit = useCallback(
    (type: RowType, materialId: number, isEdit: boolean) => {
      const result = findAction(data, type, materialId);
      if (result) {
        result.action.isEdit = isEdit;
      }
    },
    [data],
  );

  const modifyActionSpeaker = useCallback(
    (type: RowType, materialId: number, speakerId: number) => {
      const result = findAction(data, type, materialId);
      if (result) {
        result.action.speakerId = speakerId;
      }
    },
    [data],
  );

  const modifyAudioAction = useCallback(
    (material: MaterialDetail, type: RowType) => {
      const result = findAction(data, type, material.id);
      const action = result?.action;
      const {
        from,
        to,
        timeLineFrom,
        timeLineTo,
        sentence,
        sentenceUrl,
        speed,
        volume,
        generating,
      } = material;
      if (action) {
        action.from = from / 1000;
        action.to = to / 1000;
        action.start = timeLineFrom / 1000;
        action.end = timeLineTo / 1000;
        action.oriDuration = (to - from) / 1000;
        action.minDuration = (to - from) / 8000;
        action.maxDuration = (to - from) / 125;
        action.text = sentence;
        action.url = sentenceUrl;
        action.speed = speed;
        action.volume = volume;
        action.loading = generating;
      }
      if (result && action) {
        const { row, index } = result;
        const prevAction = row.actions[index - 1];
        const nextAction = row.actions[index + 1];
        if (
          (prevAction && action.start < prevAction.end) ||
          (nextAction && action.end > nextAction.start)
        ) {
          moveActionToNewRow(action, row);
        }
      }
      setData([...data]);
      return action;
    },
    [data],
  );

  // const modifySubtitleAction = useCallback(
  //   (subtitle: Subtitle, type: RowType, ignoreText = false) => {
  //     const result = findAction(data, type, subtitle.id);
  //     const action = result?.action;
  //     const { timeLineFrom, timeLineTo, text } = subtitle;
  //     if (action) {
  //       action.start = timeLineFrom / 1000;
  //       action.end = timeLineTo / 1000;
  //       if (!ignoreText) {
  //         action.text = text;
  //       }
  //     }
  //     return action;
  //   },
  //   [data],
  // );
  const updateSubtitleTime = (actions: CustomAction[], sentenceId: number) => {
    const sentenceAction = actions.filter((res) => res.sentenceId == sentenceId);
    const endTime = Math.max(...sentenceAction.map((res) => res.end));
    const startTime = Math.min(...sentenceAction.map((res) => res.start));
    actions.forEach((res) => {
      if (res.sentenceId == sentenceId) {
        res.subtitle_start = startTime;
        res.subtitle_end = endTime;
      }
    });
  };

  useEffect(() => {
    EditEventBus.on('audio-text-modified', (materialId: number, text: string) => {
      modifyActionText(RowType.audio, materialId, text);
    });
    EditEventBus.on('translation-text-modified', (materialId: number, text: string) => {
      modifyActionText(RowType.translation, materialId, text);
    });
    EditEventBus.on('audio-speaker-modified', (materialId: number, speakerId: number) => {
      modifyActionSpeaker(RowType.audio, materialId, speakerId);
    });
    EditEventBus.on('translation-speaker-modified', (materialId: number, speakerId: number) => {
      modifyActionSpeaker(RowType.translation, materialId, speakerId);
    });
    EditEventBus.on('audio-subtitle-modified', (materialId: number, text: string) => {
      modifyActionText(RowType.audioSubtitle, materialId, text);
    });
    EditEventBus.on('translation-subtitle-modified', (materialId: number, text: string) => {
      modifyActionText(RowType.translationSubtitle, materialId, text);
    });
    EditEventBus.on('material-inserted', (material: MaterialDetail, nextId: number) => {
      // 33迭代只能增加原文，其他类型先不考虑
      if (material.type === MaterialType.AUDIO) {
        const action = materialToAction(material, AudioMode.Origin);
        const subtitle = {
          ...material.subtitle,
          id: material.id,
        };
        const subtitleAction: CustomAction = materialToAction(
          subtitle as MaterialDetail,
          AudioMode.Origin,
        );
        const audioRows = data.filter(isAudioType);
        const subtitleRow = data.find(isAudioSubtitleType);
        if (nextId < 0) {
          const row = audioRows.at(-1);
          row?.actions.push(action);
          subtitleRow?.actions.push(subtitleAction);
        } else {
          for (let i = 0; i < audioRows.length; i++) {
            const row = audioRows[i];
            const index = row.actions.findIndex((a) => a.materialId === nextId);
            if (index >= 0) {
              row.actions.splice(index, 0, action);
              break;
            }
          }
          const subtitleIndex = subtitleRow!.actions.findIndex((a) => a.materialId === nextId);
          if (subtitleIndex >= 0) {
            subtitleRow?.actions.splice(subtitleIndex, 0, subtitleAction);
          }
        }
        setData([...data]);
      }
    });
    EditEventBus.on('audio-updated', (material: MaterialDetail, type: string) => {
      const audio = modifyAudioAction(material, RowType.audio);
      const { subtitles } = transformAudio(
        {
          details: [material],
          type: 3,
          oriUrl: '',
          id: 0,
        },
        0,
        editContextValue.projectDetail.mode,
      );
      const sentenceId: number[] = [];
      subtitles[0].actions.forEach(
        (res) => !sentenceId.includes(res.sentenceId!) && sentenceId.push(res.sentenceId!),
      );
      syncSubtitleAction(subtitles[0].actions, sentenceId!);

      const subtitleRow = data.find(isAudioSubtitleType);
      if (subtitleRow && audio) {
        const subtitle_row = getSentencesFromSegmenta(subtitleRow);
        const operateAction = subtitle_row.find((res) => res.sentenceId === audio.materialId);
        const subtitles = calcSubtitleOverlapWithEffect(
          { ...subtitleRow, actions: subtitle_row },
          operateAction!,
        );
        if (operateAction && subtitleRow) {
          syncSubtitle(
            audio,
            operateAction,
            subtitles.prevSubtitle,
            subtitles.nextSubtitle,
            editContextValue.projectDetail.mode,
            true,
            {
              prevSubtitleState: true,
              currentSubtitleState: true,
              nextSubtitleState: true,
            },
            type,
          );
        }
      }
    });
    EditEventBus.on('translation-updated', (material: MaterialDetail, type: string) => {
      const audio = modifyAudioAction(material, RowType.translation);
      const { subtitles } = transformAudio(
        {
          details: [material],
          type: 3,
          oriUrl: '',
          id: 0,
        },
        1,
        editContextValue.projectDetail.mode,
      );
      const sentenceId: number[] = [];
      subtitles[0].actions.forEach(
        (res) => !sentenceId.includes(res.sentenceId!) && sentenceId.push(res.sentenceId!),
      );
      syncSubtitleAction(subtitles[0].actions, sentenceId!);

      const subtitleRow = data.find(isTranslationSubtitleType);
      if (subtitleRow && audio) {
        const subtitle_row = getSentencesFromSegmenta(subtitleRow);
        const operateAction = subtitle_row.find((res) => res.sentenceId === audio.materialId);
        const subtitles = calcSubtitleOverlapWithEffect(
          { ...subtitleRow, actions: subtitle_row },
          operateAction!,
        );
        if (operateAction && subtitleRow) {
          syncSubtitle(
            audio,
            operateAction,
            subtitles.prevSubtitle,
            subtitles.nextSubtitle,
            editContextValue.projectDetail.mode,
            true,
            {
              prevSubtitleState: true,
              currentSubtitleState: true,
              nextSubtitleState: true,
            },
            type,
          );
        }
      }
    });
    EditEventBus.on('audio-generating', (material: MaterialDetail) => {
      modifyAudioAction(material, RowType.audio);
    });
    EditEventBus.on('translation-generating', (material: MaterialDetail) => {
      modifyAudioAction(material, RowType.translation);
    });
    EditEventBus.on('material-deleted', (material) => {
      let rows: CustomRow[] = [];
      switch (material.type) {
        case MaterialType.VIDEO: {
          rows = data.filter(isVideoType);
          break;
        }
        case MaterialType.MUSIC: {
          rows = data.filter(isMusicType);
          break;
        }
        case MaterialType.AUDIO:
        case MaterialType.CAPTIONS:
        case MaterialType.TRANSLATION: {
          const subtitleFilter = isTranslationSubtitle(editContextValue.projectDetail.mode)
            ? isTranslationSubtitleType
            : isAudioSubtitleType;
          rows = data.filter(isAudioType);
          rows.push(...data.filter(subtitleFilter));
          break;
        }
      }
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const index = row.actions.findIndex((a) => a.materialId === material.id);
        if (index >= 0) {
          const deleteAction = row.actions[index];
          row.actions.splice(index, 1);
          if (
            row.actions.length === 0 &&
            ![MaterialType.AUDIO, MaterialType.CAPTIONS, MaterialType.TRANSLATION].includes(
              material.type,
            )
          ) {
            const rowIndex = data.indexOf(row);
            data.splice(rowIndex, 1);
          }
          if (
            [MaterialType.CAPTIONS, MaterialType.TRANSLATION].includes(material.type) &&
            deleteAction.sentenceId !== undefined
          ) {
            // 如果是字幕的话，修改整条的时间
            updateSubtitleTime(row.actions, deleteAction.sentenceId);
          }
          setData([...data]);
        }
      }
    });
    EditEventBus.on('translation-language-changed', () => {
      initMaterialData();
      const engine = timelineState.current;
      if (engine) {
        engine.pause();
        engine.setTime(0);
        engine.reRender();
      }
    });
    EditEventBus.on('segmente-subtitle-modified', (type, segmenteSubtitle) => {
      const { subtitles } = transformAudio(
        segmenteSubtitle,
        type,
        editContextValue.projectDetail.mode,
      );
      if (subtitles[0]) {
        subtitles[0].hide = !editContextValue.subtitleShow;
        subtitles[0].classNames = editContextValue.subtitleShow ? [] : ['opacity'];
      }
      const index = data.findIndex((res) =>
        [RowType.audioSubtitle, RowType.translationSubtitle].includes(res.type),
      );
      if (index !== -1) {
        data.splice(index, 1, ...subtitles);
      }
    });
    EditEventBus.on('add-subtitle-segmented', (type, subtitle_segmented) => {
      const subtitleFilter = isTranslationSubtitle(editContextValue.projectDetail.mode)
        ? isTranslationSubtitleType
        : isAudioSubtitleType;
      const mode = isTranslationSubtitle(editContextValue.projectDetail.mode) ? 1 : 0;
      const subtitleRow = data.find(subtitleFilter);
      if (subtitleRow) {
        const othenAction = subtitleRow.actions.find(
          (res) => res.sentenceId === subtitle_segmented.subtitleId,
        );
        const newAction = materialToAction(
          {
            ...subtitle_segmented,
            type,
            id: subtitle_segmented.subtitleId,
            wordId: subtitle_segmented.id,
          } as MaterialDetail,
          mode,
          editContextValue.projectDetail.mode,
        );
        if (othenAction) {
          newAction.subtitle_end = othenAction.subtitle_end;
          newAction.subtitle_start = othenAction.subtitle_start;
        } else {
          newAction.subtitle_end = newAction.end;
          newAction.subtitle_start = newAction.start;
        }
        subtitleRow.actions.push(newAction);
        subtitleRow.actions.sort(
          (a: CustomAction, b: CustomAction) => a.start - b.start || a.end - b.end,
        );
        setData([...data]);
      }
    });
    EditEventBus.on('audio-text-edit', (materialId: number, isEdit: boolean) => {
      modifyActionEdit(RowType.audio, materialId, isEdit);
    });
    EditEventBus.on('translation-text-edit', (materialId: number, isEdit: boolean) => {
      modifyActionEdit(RowType.translation, materialId, isEdit);
    });
    EditEventBus.on('audio-generating-all', (materials: MaterialDetail[]) => {
      materials.forEach((material) => modifyAudioAction(material, RowType.audio));
    });
    EditEventBus.on('translation-generating-all', (materials: MaterialDetail[]) => {
      materials.forEach((material) => modifyAudioAction(material, RowType.translation));
    });
    EditEventBus.on('retranslation-subtitle', (material, type) => {
      const { subtitles } = transformAudio(
        {
          details: [material],
          type: 3,
          oriUrl: '',
          id: 0,
        },
        type,
        editContextValue.projectDetail.mode,
      );
      const sentenceId: number[] = [];
      subtitles[0].actions.forEach(
        (res) => !sentenceId.includes(res.sentenceId!) && sentenceId.push(Number(res.sentenceId!)),
      );
      syncSubtitleAction(subtitles[0].actions, sentenceId);
    });
    return () => {
      EditEventBus.off('audio-text-modified');
      EditEventBus.off('translation-text-modified');
      EditEventBus.off('audio-speaker-modified');
      EditEventBus.off('translation-speaker-modified');
      EditEventBus.off('audio-subtitle-modified');
      EditEventBus.off('translation-subtitle-modified');
      EditEventBus.off('material-inserted');
      EditEventBus.off('audio-updated');
      EditEventBus.off('translation-updated');
      EditEventBus.off('audio-generating');
      EditEventBus.off('translation-generating');
      EditEventBus.off('material-deleted');
      EditEventBus.off('translation-language-changed');
      EditEventBus.off('segmente-subtitle-modified');
      EditEventBus.off('add-subtitle-segmented');
      EditEventBus.off('audio-text-edit');
      EditEventBus.off('translation-text-edit');
      EditEventBus.off('audio-generating-all');
      EditEventBus.off('translation-generating-all');
      EditEventBus.off('retranslation-subtitle');
    };
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event: { target: Node | null }) => {
      const selectTarget = document.querySelector('.actionVolume');
      if (selectTarget && !selectTarget.contains(event.target)) {
        setShowChangeVolume(false);
      }
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    document.addEventListener('click', handleClickOutside);
    return () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    ActionEvent.on('action-changed', (action: CustomAction | undefined) => {
      let targetAction = action;
      if (action) {
        const row = data.find(
          (row) => row.type === action.type && row.trackIndex === action.trackIndex,
        );
        targetAction = row?.actions.find((a) => a.id === action.id);
      }
      setActionToCut(targetAction);
    });
    ActionEvent.on('changeVolume', (action: CustomAction) => {
      timelineState.current?.pause();
      let type;
      if (isMusicType(action)) {
        // BGM
        type = RowType.music;
      } else if (isTranslationType(action)) {
        // 译文句子
        type = RowType.translation;
      } else {
        // 原文句子
        type = RowType.audio;
      }
      const actionChange = findAction(data, type!, action.materialId);
      if (actionChange && actionChange.action) {
        actionChange.action.volume! = action.volume!;
        setData([...data]);
        syncMaterial(actionChange.action);
      }
    });
    return () => {
      ActionEvent.off('action-changed');
      ActionEvent.off('changeVolume');
    };
  }, [data]);

  useEffect(() => {
    if (selectedAction) {
      setCanCut(
        isSupportCut(selectedAction) && time > selectedAction.start && time < selectedAction.end,
      );
    } else {
      setCanCut(
        !!actionToCut &&
          isSupportCut(actionToCut) &&
          time > actionToCut.start &&
          time < actionToCut.end,
      );
    }
  }, [selectedAction, actionToCut, time]);

  useEffect(() => {
    const selectAction = selectChangeVolumeAction();
    setVolumeAction(selectAction);
  }, [checkChangeVolume.current, time]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.onClickGATrackEvent('timeline-zoom', {
      zoom: timelineScale,
    });
    const unit = SCALE_WIDTH / timelineScale;
    const lastTime = calcLastTime(data);
    const blockLength = lastTime * unit;
    const waveformWidth = resetWaveformPic(timelineScale);
    setWaveformCompleteWidth(waveformWidth);
    if (timeLineClientWidth.current <= blockLength) {
      const distance = unit * time;
      timelineState.current?.setScrollLeft(distance - Math.round(timeToLeft.current));
    }
  }, [timelineScale]);

  const resetWaveformPic = (sacle: number) => {
    let allTime: number = 0;
    const utilTime = SCALE_WIDTH / sacle;
    editContextValue.musicMaterial.details.forEach((detail) => {
      allTime += (detail.timeLineTo - detail.timeLineFrom) / 1000;
    });
    return allTime * utilTime;
  };

  const pickMaterial = useCallback(
    (item: CustomAction | CustomRow) => {
      const {
        videoMaterial,
        setVideoMaterial,
        musicMaterial,
        setMusicMaterial,
        sentenceMaterial,
        setSentenceMaterial,
        translationMaterial,
        setTranslationMaterial,
      } = editContextValue;

      let material: Material;
      let setMaterial: (material: Material) => void;
      switch (item.type) {
        case RowType.video: {
          material = videoMaterial;
          setMaterial = setVideoMaterial;
          break;
        }
        case RowType.music: {
          material = musicMaterial;
          setMaterial = setMusicMaterial;
          break;
        }
        case RowType.audioSubtitle:
        case RowType.audio: {
          material = sentenceMaterial;
          setMaterial = setSentenceMaterial;
          break;
        }
        case RowType.translationSubtitle:
        case RowType.translation: {
          material = translationMaterial;
          setMaterial = setTranslationMaterial;
          break;
        }
      }
      return {
        material,
        setMaterial,
      };
    },
    [
      editContextValue.videoMaterial,
      editContextValue.musicMaterial,
      editContextValue.sentenceMaterial,
      editContextValue.translationMaterial,
    ],
  );

  function submitInsert(materialDetail: MaterialDetail) {
    const { projectDetail, sessionId, useTranslate, translationLanguage } = editContextValue;
    projectController.timelineOp({
      projectId: projectDetail.id,
      body: {
        timelineOpTypeEnum: OperationType.INSERT,
        sessionId,
        isTranslation: useTranslate,
        language: translationLanguage,
        materialDetail,
        newIndex: materialDetail.id,
        usage: projectDetail.usage,
      },
    });
  }

  function submitModify(materialDetail: MaterialDetail | Subtitle) {
    const { projectDetail, sessionId, useTranslate, translationLanguage } = editContextValue;
    projectController.timelineOp({
      projectId: projectDetail.id,
      body: {
        timelineOpTypeEnum: OperationType.MODIFY,
        sessionId,
        isTranslation: useTranslate,
        language: translationLanguage,
        materialDetail,
      },
    });
  }

  function submitBatchModify(
    materialDetails: MaterialDetail[] | Subtitle[],
    isTranslationText: boolean,
  ) {
    const { projectDetail, sessionId, useTranslate, translationLanguage } = editContextValue;
    projectController.timelineOp({
      projectId: projectDetail.id,
      body: {
        timelineOpTypeEnum: OperationType.BATCH_MODIFY,
        sessionId,
        isTranslation: useTranslate,
        isTranslationText,
        language: translationLanguage,
        materialDetail: materialDetails[0],
        materialDetails,
      },
    });
  }

  function submitSubtitleSyncModify(
    materialDetails: MaterialDetail[] | Subtitle[],
    isTranslationText: boolean,
  ) {
    const { projectDetail, sessionId, useTranslate, translationLanguage } = editContextValue;
    projectController.timelineOp({
      projectId: projectDetail.id,
      body: {
        timelineOpTypeEnum: OperationType.SYNCTIME,
        sessionId,
        isTranslation: useTranslate,
        isTranslationText,
        language: translationLanguage,
        materialDetail: materialDetails[0],
        materialDetails,
      },
    });
  }

  function submitSplit(type: RowType, id: number, splitTime: number, newId: number) {
    const { projectDetail, sessionId, useTranslate, translationLanguage } = editContextValue;
    projectController.timelineOp({
      projectId: projectDetail.id,
      body: {
        timelineOpTypeEnum: OperationType.SPLIT,
        sessionId,
        isTranslation: useTranslate,
        language: translationLanguage,
        materialDetail: {
          type: TypeMap[type],
        },
        cutIndex: id,
        cutPoint: splitTime * 1000,
        newIndex: newId,
      },
    });
  }

  function submitBatchHide(type: RowType, ids: number[], isHide: boolean) {
    const { projectDetail, sessionId, useTranslate, translationLanguage } = editContextValue;
    projectController.timelineOp({
      projectId: projectDetail.id,
      body: {
        timelineOpTypeEnum: OperationType.HIDE,
        sessionId,
        isTranslation: useTranslate,
        language: translationLanguage,
        materialDetail: {
          type: TypeMap[type],
        },
        indexes: ids,
        isHide,
      },
    });
  }

  // function submitBatchVolume(type: RowType, ids: number[], volume: number) {
  //   const { projectDetail, sessionId, useTranslate, translationLanguage } = editContextValue;
  //   projectController.timelineOp({
  //     projectId: projectDetail.id,
  //     body: {
  //       timelineOpTypeEnum: OperationType.VOLUME,
  //       sessionId,
  //       isTranslation: useTranslate,
  //       language: translationLanguage,
  //       materialDetail: {
  //         type: TypeMap[type],
  //       },
  //       indexes: ids,
  //       volume,
  //     },
  //   });
  // }
  function submitTrackMuted(type: RowType, trackIndex: number, mute: boolean) {
    const { projectDetail, sessionId, translationLanguage } = editContextValue;
    projectController.timelineOp({
      projectId: projectDetail.id,
      body: {
        timelineOpTypeEnum: OperationType.MUTE,
        trackIndex,
        mute,
        sessionId,
        materialDetail: {
          type: TypeMap[type],
        },
        language: translationLanguage,
      },
    });
  }
  function submitSubtitleShow(showValue: number) {
    const { projectDetail } = editContextValue;
    projectController.updateSubtitleShow({
      projectId: projectDetail.id,
      body: {
        subtitleShowEnum: showValue,
      },
    });
  }
  const syncAnotherSubtitle = (action: CustomAction, syncSpeech: boolean = false) => {
    const type = action.type === 31 ? RowType.translationSubtitle : RowType.audioSubtitle;
    const { material, setMaterial } = pickMaterial({ ...action, type });
    const { detail } = findMaterialDetail(action, material);
    const actions = data
      .find((res) => res.type === action.type)
      ?.actions?.filter((res) => res.sentenceId === action.sentenceId);
    if (actions) {
      modifySubtitleSegmentedWidthEffect(detail, actions!);
      detail.subtitle.timeLineFrom = detail.subtitleSegmented.at(0)!.timeLineFrom;
      detail.subtitle.timeLineTo = detail.subtitleSegmented.at(-1)!.timeLineTo;
      if (syncSpeech) {
        detail.timeLineFrom = detail.subtitleSegmented.at(0)!.timeLineFrom;
        detail.timeLineTo = detail.subtitleSegmented.at(-1)!.timeLineTo;
      }
    }
    material.details.sort(
      (a, b) =>
        a.subtitle.timeLineFrom - b.subtitle.timeLineFrom ||
        a.subtitle.timeLineTo - b.subtitle.timeLineTo,
    );
    setMaterial({ ...material });
  };
  const syncMaterial = useCallback(
    (action: CustomAction, needSort: boolean = false, syncSpeech: boolean = false) => {
      const { sortByTimeLineFrom } = editContextValue;
      const { material, setMaterial } = pickMaterial(action);
      const { detail } = findMaterialDetail(action, material);
      if (detail) {
        // 如果是字幕
        if (isAudioSubtitleType(action) || isTranslationSubtitleType(action)) {
          const actions = data
            .find((res) => res.type === action.type)
            ?.actions?.filter((res) => res.sentenceId === action.sentenceId);
          if (actions) {
            updateSubtitleTime(actions, action.sentenceId!);
          }
          modifySubtitleSegmentedWidthEffect(detail, actions!);
          detail.subtitle.timeLineFrom = detail.subtitleSegmented.at(0)!.timeLineFrom;
          detail.subtitle.timeLineTo = detail.subtitleSegmented.at(-1)!.timeLineTo;
          if (syncSpeech) {
            detail.timeLineFrom = detail.subtitleSegmented.at(0)!.timeLineFrom;
            detail.timeLineTo = detail.subtitleSegmented.at(-1)!.timeLineTo;
          }
          if (canSyncSubtitle(editContextValue.projectDetail.mode)) {
            // 同步译文与原文的时间
            syncAnotherSubtitle(action, syncSpeech);
            submitSubtitleSyncModify([detail], isTranslationType(action));
          } else {
            submitModify(detail);
          }
        } else {
          modifyMaterialWidthEffect(detail, action);
          submitModify(detail);
        }

        if (needSort) {
          material.details = sortByTimeLineFrom(material.details);
        }

        setMaterial({ ...material });
      }
    },
    [
      editContextValue.videoMaterial,
      editContextValue.musicMaterial,
      editContextValue.sentenceMaterial,
      editContextValue.translationMaterial,
      data,
    ],
  );
  const subtitleAllocation = (material: MaterialDetail) => {
    const utilTime =
      (material.subtitle.timeLineTo - material.subtitle.timeLineFrom) /
      material?.subtitleSegmented?.length;
    const startTime = material.subtitle.timeLineFrom;
    material?.subtitleSegmented?.forEach((item, index) => {
      item.timeLineFrom = startTime + index * utilTime;
      item.timeLineTo = item.timeLineFrom + utilTime;
    });
    return material;
  };
  const subtitleMove = (material: MaterialDetail, diffTime: number) => {
    material.subtitleSegmented.forEach((item) => {
      item.timeLineFrom = item.timeLineFrom + diffTime;
      item.timeLineTo = item.timeLineTo + diffTime;
    });
    return material;
  };
  const reallocateWords = (
    material: MaterialDetail,
    projectMode: ProjectMode,
    action: CustomAction,
    canCut: boolean = true,
    diffTime: number = 0,
  ) => {
    let info;
    let mode = 0;
    if (projectMode === 'translation_and_tts') {
      let { detail: original } = findMaterialDetail(action, editContextValue.sentenceMaterial);
      original.subtitle.timeLineFrom = material.subtitle.timeLineFrom;
      original.subtitle.timeLineTo = material.subtitle.timeLineTo;
      const data_tran = canCut ? subtitleAllocation(material) : subtitleMove(material, diffTime);
      const data_orig = canCut ? subtitleAllocation(original) : subtitleMove(original, diffTime);
      info = data_tran;
      mode = 1;
      original = data_orig;
      editContextValue.setSentenceMaterial({ ...editContextValue.sentenceMaterial });
      // const originalSentence = editContextValue.sentenceMaterial.details.find(
      //   (res) => res.id === material.id,
      // );
      // if (originalSentence) {
      //   submitBatchModify([originalSentence], false);
      // }
    } else {
      const data_tran = canCut ? subtitleAllocation(material) : subtitleMove(material, diffTime);
      info = data_tran;
    }
    // 同步译文的时间
    const type = action.type === 4 ? RowType.translationSubtitle : RowType.audioSubtitle;
    const subtitleAction = data
      .find((res) => res.type === type)
      ?.actions.find((res) => res.sentenceId === action.materialId);
    if (subtitleAction && canSyncSubtitle(editContextValue.projectDetail.mode)) {
      syncAnotherSubtitle(subtitleAction);
    }
    const { subtitles } = transformAudio(
      {
        details: [info],
        type: 3,
        oriUrl: '',
        id: 0,
      },
      mode,
      projectMode,
    );
    return subtitles[0].actions;
  };
  const syncSubtitleAction = (actions: CustomAction[], sentenceId: number[]) => {
    const subtitleFilter = showAudioAndTranslation(editContextValue.projectDetail.mode)
      ? isTranslationSubtitleType
      : isAudioSubtitleType;
    const row = data.find(subtitleFilter);
    if (row) {
      const new_action = row?.actions?.filter(
        (res) => res.sentenceId && !sentenceId.includes(res.sentenceId),
      );
      row.actions! = [...new_action, ...actions];
      row.actions!.sort((a: CustomAction, b: CustomAction) => a.start - b.start);
    }
  };
  const getSpeechAction = (subtitle: CustomAction) => {
    let speech;
    const speechType = subtitle.type === RowType.audioSubtitle ? isAudioType : isTranslationType;
    const rows = data.filter(speechType);
    rows.some((row) => {
      speech = row.actions.find((res) => subtitle.sentenceId == res.materialId);
      return !!speech;
    });
    return speech;
  };
  const syncSubtitle = useCallback(
    (
      action: CustomAction,
      currentSubtitle: CustomAction,
      prevSubtitle: CustomAction,
      nextSubtitle: CustomAction,
      projectMode: ProjectMode,
      isTranslationText: boolean,
      subtitleNeedCut = {
        prevSubtitleState: true,
        currentSubtitleState: true,
        nextSubtitleState: true,
      },
      type: string = '',
    ) => {
      const { sortByTimeLineFrom } = editContextValue;
      action.type = showAudioAndDoubleSubtitle(projectMode) ? RowType.translation : action.type;
      const { material, setMaterial } = pickMaterial(action);
      const { detail } = findMaterialDetail(action, material);
      const new_action = [];
      const sentenceId: number[] = [];
      if (detail) {
        const diffTime = currentSubtitle.start * 1000 - detail.subtitle.timeLineFrom;
        modifyMaterialWidthEffect(detail, action);
        modifySubtitleWidthEffect(detail, currentSubtitle);
        const actions = reallocateWords(
          detail,
          projectMode,
          action,
          subtitleNeedCut.currentSubtitleState,
          diffTime,
        );
        new_action.push(...actions);
        sentenceId.push(currentSubtitle.sentenceId!);

        const subtitlesToSubmit: Subtitle[] = [];
        if (canSyncSubtitle(editContextValue.projectDetail.mode)) {
          subtitlesToSubmit.push(detail);
        } else {
          submitModify(detail);
        }
        if (prevSubtitle && subtitleNeedCut.prevSubtitleState) {
          const { detail: prevDetail } = findMaterialDetail(prevSubtitle, material);
          const prevAction = getSpeechAction(prevSubtitle);
          if (prevDetail && prevAction) {
            modifySubtitleWidthEffect(prevDetail, prevSubtitle);
            const actions = reallocateWords(prevDetail, projectMode, prevAction);
            subtitlesToSubmit.push(prevDetail);
            new_action.push(...actions);
            sentenceId.push(prevSubtitle.sentenceId!);
          }
        }
        if (nextSubtitle && subtitleNeedCut.nextSubtitleState) {
          const { detail: nextDetail } = findMaterialDetail(nextSubtitle, material);
          const nextAction = getSpeechAction(nextSubtitle);
          if (nextDetail && nextAction) {
            modifySubtitleWidthEffect(nextDetail, nextSubtitle);
            const actions = reallocateWords(nextDetail, projectMode, nextAction);
            subtitlesToSubmit.push(nextDetail);
            new_action.push(...actions);
            sentenceId.push(nextSubtitle.sentenceId!);
          }
        }
        if (subtitlesToSubmit.length > 0) {
          if (canSyncSubtitle(editContextValue.projectDetail.mode)) {
            submitSubtitleSyncModify(subtitlesToSubmit, isTranslationText);
          } else {
            submitBatchModify(subtitlesToSubmit, isTranslationText);
          }
        }
        if (type !== 'tts') {
          material.details = sortByTimeLineFrom(material.details);
        }

        // 同步到时间轴
        syncSubtitleAction(new_action, sentenceId!);
        setMaterial({ ...material });
      }
    },
    [editContextValue.sentenceMaterial, editContextValue.translationMaterial, data],
  );

  function onClickTimeArea(time: number) {
    if (editContextValue.isPlaying) {
      pause();
    }
    const lastTime = calcLastTime(data);
    if (time > lastTime) {
      timelineState.current?.setTime(lastTime);
      return false;
    }
    return true;
  }
  function onCursorDragEnd(time: number) {
    const lastTime = calcLastTime(data);
    if (time > lastTime) {
      timelineState.current?.setTime(lastTime);
    }
  }

  function addAudioAction() {
    // 除了翻译带tts不能新增，其他模式支持新增句子
    if (!showAudioAndTranslation(editContextValue.projectDetail.mode)) {
      editContextValue.setActiveTab('speech');
      const {
        speakerList,
        sentenceMaterial,
        setSentenceMaterial,
        translationMaterial,
        setTranslationMaterial,
        sortByTimeLineFrom,
      } = editContextValue;
      const actionIndexBeforeCurrent = ((details: MaterialDetail[] = []) => {
        let targetIndex = -1;
        for (let i = 0; i < details.length; i++) {
          const current = details[i];
          const next = details[i + 1];
          if (current.timeLineFrom / 1000 < time) {
            targetIndex = i;
            if (next && next.timeLineFrom / 1000 >= time) {
              break;
            }
          }
        }
        return targetIndex;
      })(sentenceMaterial.details);
      let speakerId = 0;
      if (actionIndexBeforeCurrent >= 0) {
        speakerId = sentenceMaterial.details[actionIndexBeforeCurrent].speakerId;
      } else if (sentenceMaterial.details.length === 0) {
        speakerId = speakerList[0]?.speakerId;
      }
      const newAction: CustomAction = {
        id: generateId(),
        materialId: generateMaterialId(),
        type: RowType.audio,
        start: time,
        end: time,
        from: 0,
        to: 0,
        url: '',
        text: '',
        speakerId,
        speed: 1,
        volume: 1,
        effectId: 'effect1',
      };
      const rows = data.filter(isAudioType);
      const row = rows.at(-1);
      if (row) {
        let index = 0;
        for (; index < row.actions.length; index++) {
          const currentAction = row.actions[index];
          if (currentAction.start >= time) {
            break;
          }
        }
        newAction.volume = row.volume;
        newAction.trackIndex = row.trackIndex;
        row.actions.splice(index, 0, newAction);
      }
      const subtitleAction: CustomAction = {
        id: generateId(),
        materialId: newAction.materialId,
        type: RowType.audioSubtitle,
        start: time,
        end: time,
        text: '',
        effectId: '',
      };
      const subtitleRow = data.find(isAudioSubtitleType);
      if (subtitleRow) {
        let index = 0;
        for (; index < subtitleRow.actions.length; index++) {
          const currentAction = subtitleRow.actions[index];
          if (currentAction.start >= time) {
            break;
          }
        }
        subtitleRow.actions.splice(index, 0, subtitleAction);
      }
      const material = actionToMaterial(newAction);
      material.subtitle.id = -1;
      sentenceMaterial.details.push(material);
      sentenceMaterial.details = sortByTimeLineFrom(sentenceMaterial.details);
      setSentenceMaterial({ ...sentenceMaterial });
      // 如果是翻译不带tts和加字幕翻译，则需要在译文句子新增一条新数据
      if (showAudioAndDoubleSubtitle(editContextValue.projectDetail.mode)) {
        const materialClone = {
          ...material,
          subtitle: { ...material.subtitle },
          type: MaterialType.TRANSLATION,
        };
        translationMaterial.details.push(materialClone);
        translationMaterial.details = sortByTimeLineFrom(translationMaterial.details);
        setTranslationMaterial({ ...translationMaterial });
      }
      submitInsert(material);
    }
  }

  function cutAction() {
    if (
      (selectedAction &&
        isSupportCut(selectedAction) &&
        time > selectedAction.start &&
        time < selectedAction.end) ||
      (actionToCut &&
        isSupportCut(actionToCut) &&
        time > actionToCut.start &&
        time < actionToCut.end)
    ) {
      const targetAction = (selectedAction || actionToCut) as CustomAction;
      const rowIndex = data.findIndex(
        (row) => row.type === targetAction.type && row.trackIndex === targetAction.trackIndex,
      );
      const row = data[rowIndex];
      const actionIndex = row.actions.findIndex((action) => action.id === targetAction.id);
      const originAction = row.actions[actionIndex];
      const { from, to } = originAction as Required<CustomAction>;
      const { currentEnd, currentTo, nextStart, nextFrom } = calcCutTime(originAction, time);
      const currentAction: CustomAction = {
        ...originAction,
        end: currentEnd,
        to: currentTo,
        oriDuration: currentTo - from,
      };
      const nextAction: CustomAction = {
        ...originAction,
        id: generateId(),
        materialId: generateMaterialId(),
        start: nextStart,
        from: nextFrom,
        oriDuration: to - nextFrom,
      };
      if (isSupportSpeed(originAction)) {
        currentAction.minDuration = currentAction.oriDuration! / 8;
        currentAction.maxDuration = currentAction.oriDuration! * 8;
        nextAction.minDuration = nextAction.oriDuration! / 8;
        nextAction.maxDuration = nextAction.oriDuration! * 8;
      }
      row.actions.splice(actionIndex, 1, currentAction, nextAction);
      data.splice(rowIndex, 1, row);
      setData([...data]);
      timelineState.current?.reRender();
      setSelectedAction(undefined);
      cutMaterial(currentAction, nextAction);
      submitSplit(currentAction.type, currentAction.materialId, time, nextAction.materialId);
    }
    window.onClickGATrackEvent('timeline-split');
  }

  const cutMaterial = useCallback(
    (currentAction: CustomAction, nextAction: CustomAction) => {
      const { material, setMaterial } = pickMaterial(currentAction);
      const { detail, index } = findMaterialDetail(currentAction, material);
      if (detail) {
        modifyMaterialWidthEffect(detail, currentAction);
        const nextMaterial = actionToMaterial(nextAction);
        material.details.splice(index, 1, detail, nextMaterial);
        setMaterial({ ...material });
      }
    },
    [editContextValue.videoMaterial, editContextValue.musicMaterial],
  );

  const moveActionToNewRow = useCallback(
    (action: CustomAction, row: CustomRow) => {
      action.trackIndex = calcTrackIndex(data, row);
      const actionIndex = row.actions.indexOf(action);
      const rowIndex = data.indexOf(row);
      row.actions.splice(actionIndex, 1);
      const classNames = [];
      if (isVideoType(action)) {
        classNames.push('custom-video-row');
        if (action.hide) {
          classNames.push('opacity');
        }
      } else if (isMusicType(action)) {
        classNames.push('custom-music-row');
      } else if (isAudioType(action) || isTranslationType(action)) {
        classNames.push('custom-audio-row');
      }
      if (isMusicType(action)) {
        action.mute = false;
      }
      data.splice(rowIndex, 0, {
        id: generateId(),
        type: row.type,
        actions: [action],
        trackIndex: action.trackIndex,
        hide: action.hide,
        volume: action.volume,
        rowHeight: isVideoType(action) || isMusicType(action) ? 42 : 26,
        classNames,
      });
      setData([...data]);
      addTrackIndex(action.trackIndex);
    },
    [data],
  );
  const restoreActionPosition = useCallback(
    (action: CustomAction, row: CustomRow, actionStartBeforeMoved: number) => {
      const actionIndex = row.actions.indexOf(action);
      const rowIndex = data.indexOf(row);
      const targetAction = row.actions[actionIndex];
      targetAction.end = actionStartBeforeMoved + targetAction.end - targetAction.start;
      targetAction.start = actionStartBeforeMoved;
      targetAction.id = generateId();
      data.splice(rowIndex, 1, row);
      setData([...data]);
    },
    [data],
  );

  const moveActionInCurrentRow = useCallback(
    (action: CustomAction, row: CustomRow, targetIndex: number) => {
      const index = row.actions.indexOf(action);
      row.actions.splice(index, 1);
      row.actions.splice(targetIndex, 0, action);
    },
    [],
  );

  const moveActionToAnotherRow = useCallback(
    (action: CustomAction, originRow: CustomRow, targetRow: CustomRow, targetIndex: number) => {
      if (targetRow && targetRow.type === originRow.type) {
        targetRow.actions.splice(targetIndex, 0, action);
        const index = originRow.actions.indexOf(action);
        originRow.actions.splice(index, 1);
        if (originRow.actions.length === 0) {
          const rowIndex = data.indexOf(originRow);
          data.splice(rowIndex, 1);
          setData([...data]);
        }
      }
    },
    [data],
  );

  const tryMoveActionToNextRow = useCallback(
    (action: CustomAction, originRow: CustomRow) => {
      const rowIndex = data.indexOf(originRow);
      const nextRow = data[rowIndex + 1];
      let moveSuccess = false;
      if (nextRow && nextRow.type === originRow.type) {
        const { canContain, containIndex } = canOtherRowContainAction(nextRow, action);
        if (canContain) {
          moveSuccess = true;
          moveActionToAnotherRow(action, originRow, nextRow, containIndex);
        }
      }
      return {
        moveSuccess,
        nextRow,
      };
    },
    [data],
  );

  const actionStartBeforeMoved = useRef(0);
  const actionEndBeforeResized = useRef(0);
  function calcAudioRelativeSubtitle(
    action: CustomAction,
    operation: ActionOperation,
    projectMode: ProjectMode,
    diffStart: number,
    originEnd: number,
  ) {
    let needModifySubtitle = false;
    let currentSubtitle: CustomAction | undefined;
    let operateAction: CustomAction | undefined;
    let subtitles:
      | {
          prevSubtitle: CustomAction;
          nextSubtitle: CustomAction;
        }
      | undefined;
    let subtitleFilter: undefined | ((item: CustomRow | CustomAction) => boolean);
    let subtitleTime = {
      prevSubtitleAfterTime: 0,
      currentSubtitleAfterTime: 0,
      nextSubtitleAfterTime: 0,
    };
    let subtitleNeedCut = {
      prevSubtitleState: true,
      currentSubtitleState: true,
      nextSubtitleState: true,
    };
    let canCut = false;
    if (isAudioType(action)) {
      subtitleFilter = showAudioAndDoubleSubtitle(projectMode)
        ? isTranslationSubtitleType
        : isAudioSubtitleType;
    } else if (isTranslationType(action)) {
      subtitleFilter = isTranslationSubtitleType;
    }
    if (subtitleFilter) {
      const subtitleRow = data.find(subtitleFilter);
      if (subtitleRow) {
        const targetSubtitle = subtitleRow.actions.find((a) => a.sentenceId == action.materialId);
        if (targetSubtitle) {
          needModifySubtitle = true;
          currentSubtitle = targetSubtitle;
          if (operation === ActionOperation.MOVE) {
            // calcSubtitleStartWithEffect(targetSubtitle, diffStart);
            moveSegmentedInCurrentRow(subtitleRow, targetSubtitle.sentenceId!, diffStart);
            modifySubtitleTime(subtitleRow, targetSubtitle, diffStart);
          } else if (operation === ActionOperation.RESIZE) {
            const data = {
              start: targetSubtitle.subtitle_start,
              end: targetSubtitle.subtitle_end,
              sentenceId: targetSubtitle.sentenceId,
            };
            canCut = calcSubtitleEndWithEffect(
              data as CustomAction,
              originEnd,
              action.end,
              subtitleRow,
            );
          }
          const subtitle_row = getSentencesFromSegmenta(subtitleRow);
          operateAction = subtitle_row.find((res) => res.sentenceId == action.materialId);
          subtitleTime = getSubtitleTime(subtitle_row, operateAction!);
          subtitles = calcSubtitleOverlapWithEffect(
            { ...subtitleRow, actions: subtitle_row },
            operateAction!,
          );
          subtitleNeedCut = getSubtitleNeedCut(subtitleTime, {
            ...subtitles,
            currentSubtitle: operateAction!,
          });
          subtitleNeedCut.currentSubtitleState = canCut || subtitleNeedCut.currentSubtitleState;
        }
      }
    }
    if (needModifySubtitle && subtitles && currentSubtitle) {
      syncSubtitle(
        action,
        operateAction || currentSubtitle,
        subtitles.prevSubtitle,
        subtitles.nextSubtitle,
        projectMode,
        isTranslationType(action),
        subtitleNeedCut,
      );
    } else {
      syncMaterial(action, true, operation === ActionOperation.MOVE);
    }
    setData([...data]);
  }

  // const calcSubtitleStartWithEffect = useCallback(
  //   (targetSubtitle: CustomAction, diffStart: number) => {
  //     const { start, end } = targetSubtitle;
  //     targetSubtitle.start = start + diffStart;
  //     targetSubtitle.end = end + diffStart;
  //   },
  //   [data],
  // );

  const getSubtitleTime = (subtitle_row: CustomAction[], subtitle_action: CustomAction) => {
    const subtitleTime = {
      prevSubtitleAfterTime: 0,
      currentSubtitleAfterTime: 0,
      nextSubtitleAfterTime: 0,
    };
    subtitleTime.currentSubtitleAfterTime = subtitle_action.end - subtitle_action.start;
    const currentSubtitleIndex = subtitle_row.indexOf(subtitle_action);
    console.log(currentSubtitleIndex);
    if (currentSubtitleIndex !== 0) {
      const action = subtitle_row[currentSubtitleIndex - 1];
      subtitleTime.prevSubtitleAfterTime = action.end - action.start;
    }
    if (currentSubtitleIndex < subtitle_row.length - 1) {
      const action = subtitle_row[currentSubtitleIndex + 1];
      subtitleTime.nextSubtitleAfterTime = action.end - action.start;
    }
    return subtitleTime;
  };

  const getSubtitleNeedCut = (
    subtitleTime: {
      prevSubtitleAfterTime: number;
      currentSubtitleAfterTime: number;
      nextSubtitleAfterTime: number;
    },
    subtitle_action: {
      prevSubtitle: CustomAction;
      nextSubtitle: CustomAction;
      currentSubtitle: CustomAction;
    },
  ) => {
    const state = {
      prevSubtitleState: true,
      currentSubtitleState: true,
      nextSubtitleState: true,
    };
    state.prevSubtitleState =
      subtitle_action.prevSubtitle &&
      subtitleTime.prevSubtitleAfterTime !==
        subtitle_action.prevSubtitle.end - subtitle_action.prevSubtitle.start;
    state.nextSubtitleState =
      subtitle_action.nextSubtitle &&
      subtitleTime.nextSubtitleAfterTime !==
        subtitle_action.nextSubtitle.end - subtitle_action.nextSubtitle.start;
    state.currentSubtitleState =
      subtitle_action.currentSubtitle &&
      subtitleTime.currentSubtitleAfterTime !==
        subtitle_action.currentSubtitle.end - subtitle_action.currentSubtitle.start;
    console.log(state);
    return state;
  };
  const calcSubtitleEndWithEffect = useCallback(
    (
      targetSubtitle: CustomAction,
      originAudioEnd: number,
      nowAudioEnd: number,
      subtitleRow: CustomRow,
    ) => {
      const { start: subtitleStart, end: subtitleEnd } = targetSubtitle;
      let canCut = false;
      if (
        (originAudioEnd <= subtitleEnd && nowAudioEnd >= subtitleEnd) ||
        (originAudioEnd >= subtitleEnd && nowAudioEnd <= subtitleEnd)
      ) {
        const new_end = Math.max(subtitleStart, nowAudioEnd);
        subtitleRow.actions.forEach((res) => {
          if (res.sentenceId === targetSubtitle.sentenceId) {
            res.subtitle_end! = new_end;
            canCut = true;
          }
        });
      }
      return canCut;
    },
    [data],
  );

  const calcSubtitleOverlapWithEffect = useCallback(
    (subtitleRow: CustomRow, targetSubtitle: CustomAction) => {
      const subtitleIndex = subtitleRow.actions.indexOf(targetSubtitle);
      const { canContain, containIndex, conflictIndex } = canCurrentRowContainAction(
        subtitleRow,
        targetSubtitle,
      );
      let insertIndex = subtitleIndex;
      if (canContain) {
        if (containIndex !== subtitleIndex) {
          moveActionInCurrentRow(targetSubtitle, subtitleRow, containIndex);
          insertIndex = containIndex;
        }
      } else {
        subtitleRow.actions.splice(subtitleIndex, 1);
        const conflictSubtitle = subtitleRow.actions[conflictIndex];
        const afterConflictSubtitle = subtitleRow.actions[conflictIndex + 1];
        if (targetSubtitle.start < conflictSubtitle.start) {
          targetSubtitle.end = conflictSubtitle.start;
          insertIndex = conflictIndex;
        } else if (targetSubtitle.start === conflictSubtitle.start) {
          if (targetSubtitle.end === conflictSubtitle.end) {
            conflictSubtitle.end = conflictSubtitle.start;
            insertIndex = conflictIndex;
          } else if (targetSubtitle.end < conflictSubtitle.end) {
            conflictSubtitle.start = targetSubtitle.end;
            insertIndex = conflictIndex;
          } else {
            targetSubtitle.start = conflictSubtitle.end;
            if (afterConflictSubtitle && targetSubtitle.end > afterConflictSubtitle.start) {
              targetSubtitle.end = afterConflictSubtitle.start;
            }
            insertIndex = conflictIndex + 1;
          }
        } else {
          conflictSubtitle.end = targetSubtitle.start;
          if (afterConflictSubtitle && targetSubtitle.end > afterConflictSubtitle.start) {
            targetSubtitle.end = afterConflictSubtitle.start;
          }
          insertIndex = conflictIndex + 1;
        }
        subtitleRow.actions.splice(insertIndex, 0, targetSubtitle);
      }
      const prevSubtitle = subtitleRow.actions[insertIndex - 1];
      const nextSubtitle = subtitleRow.actions[insertIndex + 1];
      return {
        prevSubtitle,
        nextSubtitle,
      };
    },
    [data],
  );

  function play() {
    if (timelineState.current) {
      timelineState.current.getTime() === calcLastTime(data) && timelineState.current.setTime(0);
      timelineState.current.play({ autoEnd: true });
      editContextValue.setIsPlaying(true);
    }
    window.onClickGATrackEvent('timeline-play');
  }

  function pause() {
    if (timelineState.current) {
      timelineState.current.pause();
      editContextValue.setIsPlaying(false);
    }
    window.onClickGATrackEvent('timeline-play');
  }

  function formatTime(ms: number) {
    const formatFn =
      editContextValue.projectDetail.duration > 60 * 60 * 1000
        ? formatFromHourToSecond
        : formatFromMinuteToSecond;
    return formatFn(ms);
  }

  function toggleRowHide(row: CustomRow) {
    if (isTranslationSubtitleType(row) || isAudioSubtitleType(row)) {
      let showValue = 0;
      if (row.hide) {
        showValue = editContextValue.projectDetail.subtitleShow;
        if (showValue === 0) {
          showValue = getDefaultSubtitleShow(editContextValue.projectDetail.mode);
        }
      }
      editContextValue.setSubtitleShow(showValue);
      submitSubtitleShow(showValue);
    } else {
      row.hide = !row.hide;
      if (!row.hide) {
        const index = row.classNames!.indexOf('opacity');
        if (index > -1) {
          row.classNames?.splice(index, 1);
        }
      } else {
        row.classNames?.push('opacity');
      }
      const ids: number[] = [];
      row.actions.forEach((action) => {
        ids.push(action.materialId);
        action.hide = row.hide;
      });
      setData([...data]);
      submitBatchHide(row.type, ids, row.hide);
    }
  }

  function toggleRowVolume(row: CustomRow) {
    checkChangeVolume.current = Date.now();
    row.mute = !row.mute;
    if (!row.mute) {
      const index = row.classNames!.indexOf('opacity');
      if (index > -1) {
        row.classNames?.splice(index, 1);
      }
    } else {
      row.classNames?.push('opacity');
    }
    row.actions.forEach((action) => {
      action.mute = row.mute;
    });
    setData([...data]);
    if (row.trackIndex != null) {
      submitTrackMuted(row.type, row.trackIndex, row.mute);
    }
  }

  function handleRowClick(time: number) {
    // setSelectedAction(undefined);
    if (!actionClicked.current) {
      const lastTime = calcLastTime(data);
      timelineState.current?.setTime(Math.min(time, lastTime));
    }
    actionClicked.current = false;
    return true;
  }

  function handleActionClick(action: CustomAction) {
    actionClicked.current = true;
    setTimeout(() => {
      setSelectedAction(action);
    });
    const { setActiveTab } = editContextValue;
    if (isVideoType(action)) {
      setActiveTab('media');
    } else if (isAudioType(action) || isTranslationType(action)) {
      setActiveTab('speech');
    } else if (isAudioSubtitleType(action) || isTranslationSubtitleType(action)) {
      setActiveTab('subtitle');
    }
    window.onClickGATrackEvent('timeline-clip-click', {
      clip_type: ACTION_CLIP_TYPE[action.type],
    });
  }

  // 展示添加句子按钮
  const showAddSpeech = (mode: ProjectMode) => {
    return (
      !showAudioAndTranslation(mode) &&
      !isProjectModelD(mode) &&
      !isProjectModelE(mode) &&
      !showAudioAndDoubleSubtitle(mode)
    );
  };
  // 展示添加字幕按钮
  const showAddSubtitle = (mode: ProjectMode) => {
    return showAudioAndDoubleSubtitle(mode) || isProjectModelD(mode) || isProjectModelE(mode);
  };

  const getTimeToLeft = () => {
    const distance = (SCALE_WIDTH / timelineScale) * time;
    timeToLeft.current = distance - timeLineScrollLeft.current;
  };

  const selectChangeVolumeAction = () => {
    const speech = data?.filter((res) => isAudioType(res) || isTranslationType(res));
    const speech_row = speech?.filter((res) => !res.mute);
    const actionList: CustomAction[] = [];
    if (speech_row.length) {
      speech_row.forEach((res) =>
        res.actions.forEach((res2) =>
          res2.start <= time && res2.end >= time ? actionList.push(res2) : '',
        ),
      );
    } else {
      // 全部关闭
      speech
        ?.filter(isAudioType)
        ?.forEach((res) =>
          res.actions?.forEach((res2: CustomAction) =>
            res2.start <= time && res2.end >= time ? actionList.push(res2) : '',
          ),
        );
    }
    actionList.sort((item1, item2) => {
      return item1.type - item2.type || item2.trackIndex! - item1.trackIndex!;
    });
    return actionList[0];
  };

  const modifySubtitleTime = (row: CustomRow, action: CustomAction, diffstart: number) => {
    row.actions.forEach((res) => {
      if (res.sentenceId === action.sentenceId) {
        res.subtitle_start! += diffstart;
        res.subtitle_end! += diffstart;
      }
    });
  };
  const moveSegmentedInCurrentRow = (
    row: CustomRow,
    sentenceId: number,
    diffTime: number,
    materialId?: number,
  ) => {
    row.actions.map((res: CustomAction) => {
      if (res.sentenceId == sentenceId && res.materialId != materialId) {
        res.start += diffTime;
        res.end += diffTime;
      }
      return res;
    });
    row.actions.sort((a: CustomAction, b: CustomAction) => a.start - b.start);
  };
  const addSubtitleAction = () => {
    const subtitleFilter = isTranslationSubtitle(editContextValue.projectDetail.mode)
      ? isTranslationSubtitleType
      : isAudioSubtitleType;
    const subtitleRow = data.find(subtitleFilter);
    let nextAction: CustomAction | undefined;
    let endTime = 0;
    let startTime = 0;
    let addSentence: undefined | boolean;
    let sentenceId: number | undefined;
    const id = Date.now();
    if (subtitleRow) {
      const nowAction = subtitleRow.actions.find(
        (action) => action.start <= time && action.end >= time,
      );
      if (nowAction) {
        nextAction = nowAction;
        const index = subtitleRow.actions.indexOf(nowAction);
        const nextSutitleAction =
          index === subtitleRow.actions.length - 1 ? undefined : subtitleRow.actions[index + 1];
        if (nowAction.end < nowAction.subtitle_end!) {
          // 添加的词
          addSentence = false;
          startTime = nowAction.end;
          endTime = Math.min(nowAction.end + 2, nextSutitleAction!.start);
          sentenceId = nowAction.sentenceId;
        } else {
          // 添加的一整段话
          addSentence = true;
          sentenceId = id;
          startTime = nowAction.end;
          endTime = nextSutitleAction
            ? Math.min(nowAction.end + 2, nextSutitleAction!.start)
            : nowAction.end + 2;
        }
      } else {
        startTime = time;
        const haveNextAction = subtitleRow.actions.some((action, index) => {
          const perAction = index === 0 ? undefined : subtitleRow.actions[index - 1];
          if (action.start < time || (perAction && perAction?.end > time)) return false;
          nextAction = action;
          addSentence =
            time < nextAction.subtitle_start! &&
            (index === 0 || (perAction && time > perAction.subtitle_end!));
          if (addSentence) {
            endTime = Math.min(nextAction.subtitle_start!, time + 2);
            sentenceId = id;
          } else if (
            perAction &&
            time < perAction.subtitle_end! &&
            perAction.sentenceId !== nextAction.sentenceId
          ) {
            endTime = Math.min(perAction.subtitle_end!, time + 2);
            sentenceId = perAction.sentenceId;
          } else {
            endTime = Math.min(nextAction.start!, time + 2);
            sentenceId = nextAction.sentenceId;
          }
          return true;
        });
        if (!haveNextAction) {
          addSentence = true;
          nextAction =
            subtitleRow.actions.at(-1) ||
            ({
              effectId: 'effect2',
              flexible: true,
              index: 0,
              movable: true,
              subtitle_end: endTime,
              subtitle_start: startTime,
              type: subtitleRow.type,
            } as CustomAction);
          endTime = time + 2;
          sentenceId = id;
        }
      }
    }
    if (subtitleRow && nextAction) {
      let newAction: CustomAction = {
        ...nextAction!,
        text: '',
        start: startTime,
        end: endTime,
        materialId: id + Date.now(),
        id: generateId(),
        sentenceId,
      };
      if (addSentence) {
        // 新增句子
        newAction = {
          ...newAction!,
          sentenceId: id,
          subtitle_end: endTime,
          subtitle_start: startTime,
        };
        addSubtitleSentenceToRow(subtitleRow, nextAction, newAction);
      } else {
        // 新增词
        addSubtitleActionToRow(subtitleRow, nextAction, newAction);
      }
    }
  };
  const addSubtitleActionToRow = (
    subtitleRow: CustomRow,
    nextAction: CustomAction,
    newAction: CustomAction,
  ) => {
    const isTranslation = isTranslationSubtitle(editContextValue.projectDetail.mode);
    let type = 0;
    const submitData = { audioDetail: {}, translationDetail: {} };
    // const nextId = subtitleRow.actions.indexOf(nextAction);
    // subtitleRow.actions.splice(nextId, 0, newAction);
    subtitleRow.actions.push(newAction);
    subtitleRow.actions.sort(
      (a: CustomAction, b: CustomAction) => a.start - b.start || a.end - b.end,
    );
    setData([...data]);
    // 同步到编辑区
    const { material, setMaterial } = pickMaterial(nextAction);
    const newSegmented = {
      id: newAction.materialId,
      subtitleId: newAction.sentenceId,
      text: newAction.text,
      timeLineFrom: newAction.start * 1000,
      timeLineTo: newAction.end * 1000,
    };
    if (material) {
      const item = material.details.find((res) => res.id === nextAction.sentenceId);
      if (item) {
        submitData[isTranslation ? 'translationDetail' : 'audioDetail'] = {
          id: item.id!,
          subtitleSegmented: [newSegmented],
          type: isTranslation ? 4 : 1,
        };
        item.subtitleSegmented.push(newSegmented);
        item.subtitleSegmented.sort(
          (a, b) => a.timeLineFrom - b.timeLineFrom || a.timeLineTo - b.timeLineTo,
        );
      }
      setMaterial({ ...material });
    }
    // 双语模式下，添加原文同步译文，添加译文同步原文
    if (
      showAudioAndTranslation(editContextValue.projectDetail.mode) ||
      showAudioAndDoubleSubtitle(editContextValue.projectDetail.mode) ||
      isProjectModelE(editContextValue.projectDetail.mode)
    ) {
      const action_ori = {
        ...nextAction,
        type: isTranslation ? RowType.audioSubtitle : RowType.translationSubtitle,
      };
      const { material: material_ori, setMaterial: setMaterial_ori } = pickMaterial(action_ori);
      const item = material_ori.details.find((res) => res.id === nextAction.sentenceId);
      if (item) {
        type = 2;
        submitData[isTranslation ? 'audioDetail' : 'translationDetail'] = {
          id: item.id,
          subtitleSegmented: [newSegmented],
          type: isTranslation ? 1 : 4,
        };
        item.subtitleSegmented.push(JSON.parse(JSON.stringify(newSegmented)));
        item.subtitleSegmented.sort(
          (a, b) => a.timeLineFrom - b.timeLineFrom || a.timeLineTo - b.timeLineTo,
        );
      }
      setMaterial_ori({ ...material_ori });
    }
    // 删除submitData的空值
    for (const key in submitData) {
      if (Object.keys((submitData as never)[key]).length === 0) {
        delete (submitData as never)[key];
      }
    }
    // 发送接口
    const { projectDetail, sessionId } = editContextValue;
    projectController.addSubtitle({
      projectId: projectDetail.id,
      body: {
        sessionId,
        type,
        ...submitData,
      },
    });
  };

  const addSubtitleSentenceToRow = (
    subtitleRow: CustomRow,
    nextAction: CustomAction,
    newAction: CustomAction,
  ) => {
    const isTranslation = isTranslationSubtitle(editContextValue.projectDetail.mode);
    let type = 1;
    const submitData = { audioDetail: {}, translationDetail: {} };
    // const nextId = subtitleRow.actions.indexOf(nextAction);
    // subtitleRow.actions.splice(nextId, 0, newAction);
    subtitleRow.actions.push(newAction);
    subtitleRow.actions.sort(
      (a: CustomAction, b: CustomAction) => a.start - b.start || a.end - b.end,
    );
    setData([...data]);
    // 同步到编辑区
    const { material, setMaterial } = pickMaterial(nextAction);
    const id = Date.now();
    const copiedObject: MaterialDetail = {
      id,
      sentence: '',
      sentenceUrl: '',
      subtitle: {
        id,
        text: '',
        timeLineFrom: newAction.subtitle_start! * 1000,
        timeLineTo: newAction.subtitle_end! * 1000,
        type: 3,
      },
      subtitleSegmented: [
        {
          id: newAction.materialId,
          subtitleId: id,
          text: newAction.text || '',
          timeLineFrom: newAction.start * 1000,
          timeLineTo: newAction.end * 1000,
        },
      ],
      timeLineFrom: newAction.subtitle_start! * 1000,
      timeLineTo: newAction.subtitle_end! * 1000,
      from: 0,
      to: 0,
      type:
        newAction.type === RowType.translationSubtitle
          ? MaterialType.TRANSLATION
          : MaterialType.AUDIO,
      vttUrl: '',
      vttString: '',
      hide: false,
      volume: 0,
      speed: 0,
      speakerId: 0,
      url: '',
      text: '',
      trackIndex: 0,
      generating: false,
      mute: false,
    };
    if (material) {
      submitData[isTranslation ? 'translationDetail' : 'audioDetail'] = copiedObject;
      material.details.push(copiedObject);
      material.details.sort(
        (a, b) =>
          a.subtitleSegmented?.[0]?.timeLineFrom - b.subtitleSegmented?.[0]?.timeLineFrom ||
          a.subtitleSegmented?.[0]?.timeLineTo - b.subtitleSegmented?.[0]?.timeLineTo,
      );
      setMaterial({ ...material });
    }
    // 双语模式下，添加原文同步译文，添加译文同步原文
    if (
      showAudioAndTranslation(editContextValue.projectDetail.mode) ||
      showAudioAndDoubleSubtitle(editContextValue.projectDetail.mode) ||
      isProjectModelE(editContextValue.projectDetail.mode)
    ) {
      type = 3;
      const newCopiedObject = {
        ...copiedObject,
        type:
          newAction.type === RowType.translationSubtitle
            ? MaterialType.AUDIO
            : MaterialType.TRANSLATION,
      };
      submitData[isTranslation ? 'audioDetail' : 'translationDetail'] = newCopiedObject;
      const action_ori = {
        ...nextAction,
        type: isTranslation ? RowType.audioSubtitle : RowType.translationSubtitle,
      };
      const { material: material_ori, setMaterial: setMaterial_ori } = pickMaterial(action_ori);
      material_ori.details.push(JSON.parse(JSON.stringify(newCopiedObject)));
      material_ori.details.sort(
        (a, b) =>
          a.subtitleSegmented?.[0]?.timeLineFrom - b.subtitleSegmented?.[0]?.timeLineFrom ||
          a.subtitleSegmented?.[0]?.timeLineTo - b.subtitleSegmented?.[0]?.timeLineTo,
      );
      setMaterial_ori({ ...material_ori });
    }
    // 删除submitData的空值
    for (const key in submitData) {
      if (Object.keys((submitData as never)[key]).length === 0) {
        delete (submitData as never)[key];
      }
    }
    // 发送接口
    const { projectDetail, sessionId } = editContextValue;
    projectController.addSubtitle({
      projectId: projectDetail.id,
      body: {
        sessionId,
        type,
        ...submitData,
      },
    });
  };
  // const resetWaveformPic = (sacle: number) => {
  //   let allTime: number = 0;
  //   const utilTime = SCALE_WIDTH / sacle;
  //   editContextValue.musicMaterial.details.forEach((detail) => {
  //     allTime += (detail.timeLineTo - detail.timeLineFrom) / 1000;
  //   });
  //   transformWaveImage(editContextValue.musicMaterial, allTime * utilTime).then(() => {
  //     setResetWaveform(Date.now());
  //   });
  // };
  return (
    <div className="timeline-component">
      <div className="top-actions">
        <div className="left">
          <button
            className="btn btn-sm bg-white text-black border-[#e5e5e5] w-8 mr-2 cut-btn"
            disabled={!canCut}
            onClick={cutAction}>
            <Icon name="icon-segmentation"></Icon>
          </button>
          {showAddSpeech(editContextValue.projectDetail.mode) && (
            <button
              className="btn btn-sm bg-white text-black border-[#e5e5e5] add-btn"
              onClick={addAudioAction}>
              <Icon name="icon-add-stroke"></Icon>
              {t('As_Edit_SpaceAddVoice334')}
            </button>
          )}

          {isProjectModelE(editContextValue.projectDetail.mode) ||
            isProjectModelD(editContextValue.projectDetail.mode) ||
            showAudioAndDoubleSubtitle(editContextValue.projectDetail.mode) || (
              <div className="actionVolume">
                <button
                  className={`btn btn-sm bg-white text-black change-volume ${showChangeVolume && 'actionVolume_action'}`}
                  disabled={
                    (!selectedAction ||
                      !(isAudioType(selectedAction) || isTranslationType(selectedAction))) &&
                    !volumeAction
                  }
                  onClick={() => {
                    timelineState.current?.pause();
                    setShowChangeVolume(true);
                  }}>
                  <Icon name="icon-openVolume"></Icon>
                </button>
                {showChangeVolume && (
                  <ChangeVolume
                    action={
                      (selectedAction &&
                        (isAudioType(selectedAction) || isTranslationType(selectedAction)) &&
                        selectedAction) ||
                      volumeAction
                    }
                  />
                )}
              </div>
            )}
          {showAddSubtitle(editContextValue.projectDetail.mode) && (
            <button
              className="btn btn-sm bg-white text-black border-[#e5e5e5] add-btn"
              onClick={addSubtitleAction}>
              <Icon name="icon-add-stroke"></Icon>
              {t('As_Edit_ExportAddSubtitle334')}
            </button>
          )}
        </div>

        <div className="middle">
          {editContextValue.isPlaying ? (
            <button className="btn btn-sm w-8 play-button" onClick={pause}>
              <Icon name="icon-pause-rounded"></Icon>
            </button>
          ) : (
            <button className="btn btn-sm w-8 play-button" onClick={play}>
              <Icon name="icon-play-rounded"></Icon>
            </button>
          )}
          <div className="time">{formatTime(time * 1000)}</div>/
          <div className="time">{formatTime(calcLastTime(data) * 1000)}</div>
        </div>

        <div className="right">
          <TimelineScaleController
            scale={timelineScale}
            maxscale={scaleList.at(0)!}
            minscale={scaleList.at(-1)!}
            onChange={(sacle) => {
              // 波形图图片方案-start
              // resetWaveformPic(sacle);
              // 波形图图片方案-end
              setTimelineScale(sacle);
            }}
            timeToLeft={getTimeToLeft}></TimelineScaleController>
        </div>
      </div>

      <div className="bottom-wrapper">
        <div ref={leftActions} className="left-actions">
          {data.map((item) => (
            <RowOperation
              row={item}
              key={item.id}
              visibleFn={toggleRowHide}
              volumeFn={toggleRowVolume}
              mode={editContextValue.projectDetail.mode}></RowOperation>
          ))}
        </div>

        <Timeline
          ref={timelineState}
          onChange={(data) => {
            setData(data as CustomRow[]);
          }}
          scale={timelineScale}
          scaleWidth={SCALE_WIDTH}
          startLeft={5}
          editorData={data}
          effects={effects}
          hideCursor={false}
          autoScroll={true}
          dragLine={true}
          onClickTimeArea={onClickTimeArea}
          onCursorDragEnd={onCursorDragEnd}
          disableDrag={disableDrag}
          onScroll={({ scrollTop, scrollLeft, clientWidth }) => {
            if (leftActions.current) {
              leftActions.current.scrollTop = scrollTop;
            }
            timeLineScrollLeft.current = scrollLeft;
            timeLineClientWidth.current = clientWidth - 5;
          }}
          getScaleRender={(scale) => {
            return <CustomScaleRenderer scale={scale}></CustomScaleRenderer>;
          }}
          getActionRender={(action) => {
            return (
              <CustomRenderActions
                action={action as CustomAction}
                isSelected={action.id === selectedAction?.id}
                isDragging={action.id === draggingAction?.id}
                isResized={action.id === resizedAction?.id}
                isVolumeChange={action.id === volumeAction?.id}
                resizedDirection={resizedDirection}
                disableResize={action.end - action.start <= (timelineScale / SCALE_WIDTH) * 8}
                timelineScale={timelineScale}
                onSelectedAction={(action: CustomAction) => setSelectedAction(action)}
                speakerColorList={editContextValue.speakerColorList}
                resetWaveform={resetWaveform}
                waveformCompleteWidth={waveformCompleteWidth}
                handleDisableDrag={(state) => setDisableDrag(state)}></CustomRenderActions>
            );
          }}
          onClickRow={(_, { time }) => {
            handleRowClick(time);
          }}
          onClickAction={(_, { action }) => {
            handleActionClick(action as CustomAction);
          }}
          onActionResizeStart={(params) => {
            actionEndBeforeResized.current = params.action.end;
            const action = params.action as CustomAction;
            setResizedAction(action);
            setSelectedAction(action);
          }}
          onActionResizing={(params) => {
            const action = params.action as CustomAction;
            const row = params.row;
            const { start, end } = params;
            setResizedDirection(params.dir);
            if (params.dir === 'left') {
              return false;
            } else if (isVideoType(action)) {
              // 限制视频缩放
              const oriDuration = action.oriDuration as number;
              const duration = end - start;
              return duration >= 0.2 && duration <= oriDuration;
            } else if (isSupportSpeed(action)) {
              // 限制人声、音乐缩放
              const duration = end - start;
              const { minDuration, maxDuration } = action as Required<CustomAction>;
              return duration >= minDuration && duration <= maxDuration;
            } else if (isMusicType(action)) {
              const oriDuration = action.oriDuration as number;
              const duration = end - start;
              return duration >= oriDuration * 0.125 && duration <= oriDuration;
            } else if (isSubtitle(action)) {
              const actionIndex = row.actions.indexOf(action);
              const duration = end - start;
              return (
                duration >= 0.2 &&
                (actionIndex >= row.actions.length - 1 || end <= row.actions[actionIndex + 1].start)
              );
            } else {
              const duration = end - start;
              return duration >= 0.2;
            }
          }}
          onActionResizeEnd={(params) => {
            setResizedAction(undefined);
            setResizedDirection('');
            const row = params.row as CustomRow;
            const action = params.action as CustomAction;
            action.start = Number(params.start.toFixed(3));
            action.end = Number(params.end.toFixed(3));
            if (isSupportNewRow(action)) {
              const isOverlap = isActionOverlapNeighbor(row, action);
              if (isOverlap) {
                moveActionToNewRow(action, row);
              } else if (row.actions.length === 1) {
                const { moveSuccess, nextRow } = tryMoveActionToNextRow(action, row);
                if (moveSuccess) {
                  action.hide = nextRow.hide;
                  action.volume = nextRow.volume;
                  action.trackIndex = nextRow.trackIndex;
                }
              }
            }

            // 计算音乐类的播放速度
            if (isSupportSpeed(action)) {
              const { start, end, oriDuration } = action as Required<CustomAction>;
              action.speed = oriDuration / (end - start);
            }
            calcAudioRelativeSubtitle(
              action,
              ActionOperation.RESIZE,
              editContextValue.projectDetail.mode,
              0,
              actionEndBeforeResized.current,
            );
            window.onClickGATrackEvent('timeline-clip-scale', {
              clip_type: ACTION_CLIP_TYPE[action.type],
            });
          }}
          onActionMoveStart={(params) => {
            const action = params.action as CustomAction;
            setDraggingAction(action);
            actionStartBeforeMoved.current = action.start;
            setSelectedAction(action);
          }}
          onActionMoveEnd={(params) => {
            setDraggingAction(undefined);
            const row = params.row as CustomRow;
            const action = params.action as CustomAction;
            action.start = Number(params.start.toFixed(3));
            action.end = Number(params.end.toFixed(3));
            const diffStart = action.start - actionStartBeforeMoved.current;
            let isExceedTimeLine = false;
            if (isSubtitle(action)) {
              modifySubtitleTime(row, action, diffStart);
              isExceedTimeLine =
                row.actions.find((res) => res.sentenceId === action.sentenceId)!.subtitle_start! <
                0;
            }
            const { canContain, containIndex } = canCurrentRowContainAction(row, action);
            const actionIndex = row.actions.indexOf(action);
            if (canContain && !isExceedTimeLine) {
              if (row.actions.length === 1) {
                const { moveSuccess, nextRow } = tryMoveActionToNextRow(action, row);
                if (moveSuccess) {
                  action.hide = nextRow.hide;
                  // action.volume = nextRow.volume;
                  action.trackIndex = nextRow.trackIndex;
                  action.mute = nextRow.mute;
                }
              } else if (isSubtitle(action)) {
                moveSegmentedInCurrentRow(row, action.sentenceId!, diffStart, action.materialId);
              } else if (containIndex !== actionIndex) {
                moveActionInCurrentRow(action, row, containIndex);
              }
            } else if (isSubtitle(action) && canSubtitleMove(editContextValue.projectDetail.mode)) {
              modifySubtitleTime(row, action, -diffStart);
              restoreActionPosition(action, row, actionStartBeforeMoved.current);
            } else {
              moveActionToNewRow(action, row);
            }
            calcAudioRelativeSubtitle(
              action,
              ActionOperation.MOVE,
              editContextValue.projectDetail.mode,
              diffStart,
              0,
            );
            window.onClickGATrackEvent('timeline-clip-drag', {
              clip_type: ACTION_CLIP_TYPE[action.type],
            });
          }}
        />
      </div>
    </div>
  );
}
